'use client'

import React from 'react'
import Link from 'next/link'
import {cn} from '../../../../lib/utils'

interface BottomNavigationItem {
    id: string

    label: string

    href: string

    icon: React.ReactNode

    notificationCount?: number

    disabled?: boolean
}

interface BottomNavigationProps {
    items: BottomNavigationItem[]

    activeItemId: string

    className?: string

    ariaLabel?: string
}

export const BottomNavigation = React.memo<BottomNavigationProps>(({
                                                                       items,
                                                                       activeItemId,
                                                                   className,
                                                                   ariaLabel = 'Primary Navigation'
                                                                   }) => {

    const navigationClasses = cn(
        'fixed bottom-0 left-0 right-0 z-50',
        'bg-white/90 backdrop-blur-md border-t border-gray-200 dark:bg-gray-950/90 dark:border-gray-700',
        'flex items-center justify-around min-h-[64px] sm:min-h-[68px] pt-2',
        // Fixed bars are not inside the body's inset padding, so they clear the home indicator and landscape notch themselves.
        'pb-[calc(0.5rem+env(safe-area-inset-bottom,0px))]',
        'pl-[calc(0.75rem+env(safe-area-inset-left,0px))] pr-[calc(0.75rem+env(safe-area-inset-right,0px))]',
        'sm:pl-[calc(1rem+env(safe-area-inset-left,0px))] sm:pr-[calc(1rem+env(safe-area-inset-right,0px))]',
        className
    )

    return (
        <nav data-app-navigation=""
            className={navigationClasses}
            role="navigation"
            aria-label={ariaLabel}
            data-testid="bottom-navigation"
        >
            {items.map((item) => {
                const isActive = activeItemId === item.id

                const itemClasses = cn(
                    'flex flex-col items-center justify-center',
                    'min-h-[44px] min-w-[44px] px-2 py-1',
                    'sm:min-h-[48px] sm:min-w-[48px] sm:px-3 sm:py-2',
                    'flex-1',
                    'gap-0.5',
                    'bg-transparent border-none outline-hidden cursor-pointer',
                    'font-medium font-sans text-center select-none',
                    'transition-[scale,background-color,color,box-shadow] duration-press ease-out',
                    'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
                    !item.disabled && [
                        'active:scale-[0.97]',
                        'motion-reduce:active:scale-100'
                    ],
                    'touch-manipulation',
                    isActive && [
                        'text-primary-600',
                        'dark:text-primary-400'
                    ],
                    !isActive && [
                        'text-gray-500',
                        'hover:text-gray-700',
                        'dark:text-gray-400',
                        'dark:hover:text-gray-300'
                    ],
                    item.disabled && [
                        'opacity-50',
                        'cursor-not-allowed',
                        'pointer-events-none'
                    ]
                )

                if (item.disabled) {
                    return (
                        <span
                            key={item.id}
                            id={`nav-item-${item.id}`}
                            className={itemClasses}
                            aria-label={item.label}
                            aria-disabled="true"
                            tabIndex={-1}
                            data-testid={`bottom-nav-item-${item.id}`}
                        >
                            <div className="relative flex items-center justify-center">
                                <span className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center">
                                    {item.icon}
                                </span>
                                {item.notificationCount && item.notificationCount > 0 && (
                                    <span className={cn(
                                        'absolute -top-1 -right-1',
                                        'min-w-[14px] h-3.5 px-1 py-0',
                                        'text-[9px] leading-none font-medium',
                                        'bg-red-500 text-white rounded-full',
                                        'border border-white dark:border-gray-900',
                                        'flex items-center justify-center'
                                    )}>
                                        {item.notificationCount > 99 ? '99+' : item.notificationCount}
                                    </span>
                                )}
                            </div>
                            <span className={cn(
                                'text-[9px] sm:text-[10px] leading-tight text-center',
                                'w-full max-w-full',
                                'truncate',
                                isActive && 'font-semibold'
                            )}>
                                {item.label}
                            </span>
                        </span>
                    )
                }

                return (
                    <Link
                        key={item.id}
                        id={`nav-item-${item.id}`}
                        href={item.href}
                        className={itemClasses}
                        aria-label={item.label}
                        aria-current={isActive ? 'page' : undefined}
                        data-testid={`bottom-nav-item-${item.id}`}
                    >
                        <div className="relative flex items-center justify-center">
                            <span className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center">
                                {item.icon}
                            </span>
                            {item.notificationCount && item.notificationCount > 0 && (
                                <span className={cn(
                                    'absolute -top-1 -right-1',
                                    'min-w-[14px] h-3.5 px-1 py-0',
                                    'text-[9px] leading-none font-medium',
                                    'bg-red-500 text-white rounded-full',
                                    'border border-white dark:border-gray-900',
                                    'flex items-center justify-center'
                                )}>
                                    {item.notificationCount > 99 ? '99+' : item.notificationCount}
                                </span>
                            )}
                        </div>
                        <span className={cn(
                            'text-[9px] sm:text-[10px] leading-tight text-center',
                            'w-full max-w-full',
                            'truncate',
                            isActive && 'font-semibold'
                        )}>
                            {item.label}
                        </span>
                    </Link>
                )
            })}
        </nav>
    )
})

BottomNavigation.displayName = 'BottomNavigation'
